import axios from 'axios';
import { IMPROVE_CONFIG } from '@/config.js';

// Public purchase flow ("Buy Now"). No account exists until payment is
// verified server-side; these endpoints only track the purchase itself.
export default {
  // Starts a purchase and returns { uuid, status: "started" }.
  start: function(details) {
    return axios.post(IMPROVE_CONFIG.API_URL + '/public/purchase/start', {
      plan: details.plan,
      company_name: details.company_name,
      registered_address: details.registered_address,
      contact_name: details.contact_name,
      contact_email: details.contact_email,
      first_name: details.first_name,
      last_name: details.last_name,
      phone: details.phone,
      refid: details.refid,
    });
  },
  // Returns { html, sha256_unsigned }. The html is a complete standalone
  // document of the Master Service Agreement.
  contract: function(uuid) {
    return axios.get(IMPROVE_CONFIG.API_URL + '/public/purchase/' + uuid + '/contract');
  },
  // Signs the agreement. 409 means already signed; 410 means expired.
  sign: function(uuid, signerName) {
    return axios.post(IMPROVE_CONFIG.API_URL + '/public/purchase/' + uuid + '/sign', {
      signer_name: signerName,
      agree: true,
    });
  },
  // "Continue to payment": a Stripe Checkout session (card, Apple Pay, Google
  // Pay). Returns { checkout_url } to redirect to. Nothing is created until the
  // buyer pays, and a repeat call reopens the same open session. 503 means
  // payments are not enabled yet; 409 means the purchase has moved on.
  checkout: function(uuid) {
    return axios.post(IMPROVE_CONFIG.API_URL + '/public/purchase/' + uuid + '/checkout', {});
  },
  // "Pay by bank transfer instead": issues the invoice (30 day terms, emailed
  // once). Returns { status: "invoice_sent", invoice_url, invoice_number,
  // amount_due_pence, due_at }. Idempotent: a second call returns the same invoice.
  invoice: function(uuid) {
    return axios.post(IMPROVE_CONFIG.API_URL + '/public/purchase/' + uuid + '/invoice', {});
  },
  // Returns { status, has_company }. Statuses: started, contract_signed,
  // payment_pending (a Stripe Checkout is open), invoice_sent (bank transfer
  // invoice issued), provisioned, expired.
  // Always asks the server: when the page is reloaded by Back, browsers answer
  // its requests from their cache even though the api says no-cache, and an
  // old status showed the card choice again after the buyer had chosen bank
  // transfer (2 Oct 2026). The unique parameter makes a cache hit impossible.
  status: function(uuid) {
    return axios.get(IMPROVE_CONFIG.API_URL + '/public/purchase/' + uuid + '/status', {
      params: { _: Date.now() },
    });
  },
  // Returns the sellable catalogue sourced from Stripe:
  // { plans: [{ slug, name, amount_pence, currency, interval, users_label,
  //   banding_label }, ...] sorted for display,
  //   launch_fee: { name, amount_pence } | null }.
  // 503 means the catalogue is unavailable; callers keep their display fallback.
  catalogue: function() {
    return axios.get(IMPROVE_CONFIG.API_URL + '/public/purchase/catalogue');
  },

  // Direct browser URL for the signed contract pdf (served as an attachment;
  // 409 before signing). Used as a plain <a href>, not an XHR.
  contractPdfUrl: function(uuid) {
    return IMPROVE_CONFIG.API_URL + '/public/purchase/' + uuid + '/contract.pdf';
  },

  // Storage access can throw (browsers with all site data blocked). The buy
  // flow must degrade to in-page state, never crash a lifecycle hook.
  storageGet: function(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  },
  storageSet: function(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* noop */ }
  },
  storageRemove: function(key) {
    try { window.sessionStorage.removeItem(key); } catch (e) { /* noop */ }
  },
};
